import { useFieldArray, UseFormReturn } from "react-hook-form";
import { InsuranceEntity, TypologyEnum } from "../../entity";
import React, { useCallback, useEffect, useMemo, useState, memo } from "react";
import { Button, Col, FormControl, FormGroup, FormLabel, Row } from "react-bootstrap";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { ErrorField } from "../../styles";
import { ErrorMessage } from "@hookform/error-message";
import { TooltipComponent } from "./TooltipComponent";

const messages = defineMessages({
  typology: { id: "insurance.form.typology", defaultMessage: "Typology" },
  typologySection: { id: "insurance.form.typologySection", defaultMessage: "Section {index}" },
  typologySectionAmountFrom: {
    id: "insurance.form.typologySectionAmountFrom",
    defaultMessage: "Amount From",
  },
  typologySectionAmountTo: {
    id: "insurance.form.typologySectionAmountTo",
    defaultMessage: "Amount To",
  },
  typologySectionPercentageAmount: {
    id: "insurance.form.typologySectionAmountPercentage",
    defaultMessage: "Amount Percentage",
  },
  typologySectionAmount: { id: "insurance.form.typologySectionAmount", defaultMessage: "Amount" },
  percentageTypology: { id: "insurance.form.percentageTypology", defaultMessage: "Percentage" },
  amountTypology: { id: "insurance.form.amountTypology", defaultMessage: "Amount" },
  addSection: { id: "insurance.form.addSection", defaultMessage: "+ Add Section" },
  required: {
    id: "insurance.form.typologySection.required",
    defaultMessage: "This field is required",
  },
  min: { id: "insurance.form.typologySection.min", defaultMessage: "The value must be at least 1" },
  shouldBeAtLeastZero: {
    id: "insurance.form.typologySection.shouldBeAtLeastZero",
    defaultMessage: "The value must be at least 0",
  },
  shouldBeLessThanAmountTo: {
    id: "insurance.form.typologySection.shouldBeLessThanAmountTo",
    defaultMessage: "Amount From should be less than Amount To",
  },
  shouldBeGreaterThanAmountFrom: {
    id: "insurance.form.typologySection.shouldBeGreaterThanAmountFrom",
    defaultMessage: "Amount To should be greater than Amount From",
  },
  shouldBeGreaterThanAmountToBeforeThis: {
    id: "insurance.form.typologySection.shouldBeGreaterThanAmountToBeforeThis",
    defaultMessage: "Amount From should be greater than Amount To of the previous section",
  },
  shouldBeLessThanAmountFromBeforeThis: {
    id: "insurance.form.typologySection.shouldBeLessThanAmountFromBeforeThis",
    defaultMessage: "Amount To should be less than Amount From of the previous section",
  },
  cannotAddSectionWithFullCoverage: {
    id: "insurance.form.typologySection.cannotAddSectionWithFullCoverage",
    defaultMessage: "The final amount must be greater than 0 to add a new section",
  },
});

const initialSection = { amountFrom: 0, amountTo: 0, value: 0 };

export const SectionComponent = memo(
  (props: UseFormReturn<InsuranceEntity> & { mode: "add" | "edit" | "duplicate" }) => {
    const intl = useIntl();
    const [type, setType] = useState<TypologyEnum>(props.getValues("type"));

    const { fields, append, remove, replace } = useFieldArray({
      control: props.control,
      name: `typology.${type}.sectionsList`,
    });

    const isDisabled = useMemo(() => ["edit", "duplicate"].includes(props.mode), [props.mode]);

    const onAddSection = useCallback(() => {
      const lastIndex = fields.length - 1;
      const lastSection = props.getValues(`typology.${type}.sectionsList.${lastIndex}`);

      const amountTo = Number(lastSection?.amountTo);
      if (amountTo === 0) {
        props.setError(`typology.${type}.sectionsList.${lastIndex}.amountTo`, {
          type: "manual",
          message: intl.formatMessage({
            id: "insurance.form.typologySection.cannotAddSectionWithFullCoverage",
            defaultMessage: "The final amount must be greater than 0 to add a new section",
          }),
        });
        return;
      }

      append({ amountFrom: 0, amountTo: 0, value: 0 });
    }, [append, fields.length, intl, props, type]);

    const onRemoveSection = useCallback(() => {
      if (fields.length > 1) {
        remove(fields.length - 1);
      }
    }, [fields.length, remove]);

    const onChangeType = useCallback(
      (typology: TypologyEnum) => {
        props.resetField("typology", {
          defaultValue: {
            [typology]: {
              sectionsList: [initialSection],
            },
          },
        });
        setType(typology);
      },
      [props]
    );

    useEffect(() => {
      if (props.mode === "add") {
        replace([initialSection]);
      }
    }, [type, replace, props.mode]);

    const validateAmountFromSection = useCallback(
      (index: number, value: number | undefined) => {
        const valueAsNumber = Number(value);
        const amountTo = Number(props.getValues(`typology.${type}.sectionsList.${index}.amountTo`));
        const previousAmountTo =
          index > 0
            ? Number(props.getValues(`typology.${type}.sectionsList.${index - 1}.amountTo`))
            : null;

        if (valueAsNumber < 0) {
          return intl.formatMessage(messages.shouldBeAtLeastZero);
        }

        // First section: only check if it's less than its own amountTo
        if (index === 0) {
          return valueAsNumber <= amountTo || intl.formatMessage(messages.shouldBeLessThanAmountTo);
        }

        // Subsequent sections: check against both previous amountTo and current amountTo
        if (previousAmountTo !== null && valueAsNumber <= previousAmountTo) {
          return intl.formatMessage(messages.shouldBeGreaterThanAmountToBeforeThis);
        }

        if (amountTo > 0 && valueAsNumber >= amountTo) {
          return intl.formatMessage(messages.shouldBeLessThanAmountTo);
        }

        return true;
      },
      [intl, type, props]
    );

    const validateAmountToSection = useCallback(
      (index: number, value: number | undefined) => {
        const valueAsNumber = Number(value);
        const amountFrom = Number(
          props.getValues(`typology.${type}.sectionsList.${index}.amountFrom`)
        );

        if (valueAsNumber < 0) {
          return intl.formatMessage(messages.shouldBeAtLeastZero);
        }

        // First section: amountTo can be 0 (meaning "and above") but must be >= amountFrom
        if (index === 0) {
          return (
            valueAsNumber >= amountFrom ||
            intl.formatMessage(messages.shouldBeGreaterThanAmountFrom)
          );
        }

        // Subsequent sections: if amountTo is specified (> 0), it must be greater than amountFrom
        if (valueAsNumber > 0 && valueAsNumber <= amountFrom) {
          return intl.formatMessage(messages.shouldBeGreaterThanAmountFrom);
        }

        return true;
      },
      [intl, type, props]
    );

    return (
      <>
        <FormGroup as={Col}>
          <FormLabel column sm={12} className="font-weight-bold">
            <FormattedMessage {...messages.typology} />
          </FormLabel>
          <Col sm={3}>
            <div className="SelectGroup TypologyType">
              <select
                className="form-control"
                value={type}
                onChange={e => onChangeType(e.target.value as TypologyEnum)}
                disabled={isDisabled}>
                {Object.values(TypologyEnum).map(key => (
                  <option value={key} key={key} selected={type === key}>
                    {intl.formatMessage(messages[`${key}Typology`])}
                  </option>
                ))}
              </select>
            </div>
          </Col>
        </FormGroup>
        <FormGroup as={Col}>
          {fields.map((field, index) => (
            <div className="mb-3" key={`section-${index}`}>
              <FormLabel column sm={12} className="font-weight-bold">
                <FormattedMessage {...messages.typologySection} values={{ index: index + 1 }} />
                {index === 0 && <TooltipComponent />}
              </FormLabel>
              <Col sm={12}>
                <Row>
                  <Col sm={2}>
                    <FormLabel>
                      <FormattedMessage {...messages.typologySectionAmountFrom} />
                    </FormLabel>
                    <FormControl
                      key={field.id}
                      type="number"
                      step="0.01"
                      {...props.register(`typology.${type}.sectionsList.${index}.amountFrom`, {
                        validate: value => validateAmountFromSection(index, value),
                      })}
                    />
                    <ErrorMessage
                      errors={props.formState.errors}
                      name={`typology.${type}.sectionsList.${index}.amountFrom`}
                      render={({ message }) => <ErrorField>{message}</ErrorField>}
                    />
                  </Col>
                  <Col sm={2}>
                    <FormLabel>
                      <FormattedMessage {...messages.typologySectionAmountTo} />
                    </FormLabel>
                    <FormControl
                      key={field.id}
                      type="number"
                      step="0.01"
                      {...props.register(`typology.${type}.sectionsList.${index}.amountTo`, {
                        validate: value => validateAmountToSection(index, value),
                      })}
                    />
                    <ErrorMessage
                      errors={props.formState.errors}
                      name={`typology.${type}.sectionsList.${index}.amountTo`}
                      render={({ message }) => <ErrorField>{message}</ErrorField>}
                    />
                  </Col>
                  <Col sm={2}>
                    <FormLabel>
                      {type === "percentage" ? (
                        <FormattedMessage {...messages.typologySectionPercentageAmount} />
                      ) : (
                        <FormattedMessage {...messages.typologySectionAmount} />
                      )}
                    </FormLabel>
                    <FormControl
                      key={field.id}
                      type="number"
                      step="0.01"
                      {...props.register(`typology.${type}.sectionsList.${index}.value`, {
                        min: { value: 1, message: intl.formatMessage(messages.min) },
                      })}
                    />
                    <ErrorMessage
                      errors={props.formState.errors}
                      name={`typology.${type}.sectionsList.${index}.value`}
                      render={({ message }) => <ErrorField>{message}</ErrorField>}
                    />
                  </Col>
                  {fields.length === index + 1 && (
                    <Col sm={2} className="align-content-end">
                      <Button className="btn-sm mb-1" onClick={onRemoveSection}>
                        <FontAwesomeIcon icon={faTrashCan} />
                      </Button>
                    </Col>
                  )}
                </Row>
              </Col>
            </div>
          ))}
          <Button className="btn-sm ml-3 mt-1" onClick={onAddSection}>
            <FormattedMessage {...messages.addSection} />
          </Button>
        </FormGroup>
      </>
    );
  }
);
